import React, { useState, useEffect } from 'react';

/**
 * QuestionSPTPotensiKerja Component
 * Used for: SPT Potensi Kerja (Work Potential Test)
 * Supports: TEXT mode and IMAGE mode
 *
 * Format based on PDF:
 * - Top row: 5 small images/text (A-E) as reference pattern
 * - Bottom row: 4 large images/text as answer options
 * - User must select the option that is NOT in the top row
 */
const QuestionSPTPotensiKerja = ({
    question,
    answer,
    onAnswer,
    disabled = false,
    examType = 'spt' // 'spt' or 'pt'
}) => {
    const [selectedAnswer, setSelectedAnswer] = useState(answer || '');

    useEffect(() => {
        setSelectedAnswer(answer || '');
    }, [answer]);

    // Determine if this is IMAGE mode or TEXT mode
    const masterType = question.master_type || 'text';
    const masterData = question.master_data || {};

    // Parse kolom number from question
    const kolomNumber = question.kolom || '?';

    const handleAnswer = (value) => {
        if (disabled) return;

        setSelectedAnswer(value);
        onAnswer(question.id, value);
    };

    /**
     * Render IMAGE mode
     * Top row: 5 images A-E
     * Bottom row: 4 answer options
     */
    const renderImageMode = () => {
        const images = masterData.images || [];
        const pilihan = question.pilihan || [];

        return (
            <div className="spt-potensi-kerja-image">
                {/* Kolom Header */}
                <div className="kolom-header mb-6 text-center">
                    <h2 className="text-3xl font-bold text-gray-800">
                        Kolom {kolomNumber}
                    </h2>
                </div>

                {/* Top Row - Reference Pattern (A-E) */}
                <div className="reference-pattern mb-8 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                    <div className="reference-pattern-row d-flex justify-content-center align-items-end flex-nowrap gap-3" style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                        {images.slice(0, 5).map((img, index) => (
                            <div key={index} className="pattern-item text-center" style={{ flex: '0 0 auto', width: '140px' }}>
                                <div
                                    className="image-wrapper mb-2 bg-white rounded border border-gray-300 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        padding: '8px',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        margin: '0 auto'
                                    }}
                                >
                                    <img
                                        src={`/${img.path}`}
                                        alt={img.label}
                                        className="potensi-kerja-pattern-img"
                                        style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/images/placeholder.png';
                                        }}
                                    />
                                </div>
                                <div className="label font-bold text-xl text-gray-700">
                                    {img.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Instruction */}
                <div className="instruction mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <p className="text-sm text-blue-800">
                        <strong>Pertanyaan:</strong> Pilih gambar di bawah yang <strong>TIDAK ADA</strong> pada pola di atas
                    </p>
                </div>

                {/* Bottom Row - Answer Options */}
                <div className="answer-options">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Pertanyaan</h3>

                    {/* Desktop: Grid 4 columns */}
                    <div className="hidden md:grid md:grid-cols-4 gap-6 mb-6">
                        {pilihan.slice(0, 4).map((optionPath, index) => {
                            const optionLabel = String.fromCharCode(65 + index); // A, B, C, D
                            const isSelected = selectedAnswer === optionLabel;

                            return (
                                <div
                                    key={index}
                                    className={`option-card cursor-pointer transition-all duration-200 ${
                                        isSelected
                                            ? 'ring-4 ring-blue-500 bg-blue-50'
                                            : 'hover:ring-2 hover:ring-blue-300'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => handleAnswer(optionLabel)}
                                >
                                    <div className="option-content p-4 bg-white rounded-lg border-2 border-gray-300 h-full">
                                        <div className="image-wrapper mb-3 flex items-center justify-center" style={{ minHeight: '150px' }}>
                                            <img
                                                src={`/${optionPath}`}
                                                alt={`Option ${optionLabel}`}
                                                className="max-w-full h-auto object-contain"
                                                style={{ maxHeight: '180px' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/images/placeholder.png';
                                                }}
                                            />
                                        </div>
                                        <div className={`option-label text-center p-3 rounded font-bold text-xl ${
                                            isSelected
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {optionLabel}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Mobile: Single column */}
                    <div className="md:hidden space-y-4">
                        {pilihan.slice(0, 4).map((optionPath, index) => {
                            const optionLabel = String.fromCharCode(65 + index);
                            const isSelected = selectedAnswer === optionLabel;

                            return (
                                <div
                                    key={index}
                                    className={`option-card cursor-pointer p-4 rounded-lg border-2 transition-all ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-300 hover:border-blue-300'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => handleAnswer(optionLabel)}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`label-badge flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                                            isSelected
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {optionLabel}
                                        </div>
                                        <div className="image-wrapper flex-grow flex justify-center">
                                            <img
                                                src={`/${optionPath}`}
                                                alt={`Option ${optionLabel}`}
                                                className="max-w-full h-auto object-contain"
                                                style={{ maxHeight: '120px' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/images/placeholder.png';
                                                }}
                                            />
                                        </div>
                                        {isSelected && (
                                            <div className="checkmark text-3xl text-blue-500">
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Answer buttons for mobile */}
                <div className="answer-buttons md:hidden mt-6 grid grid-cols-4 gap-3">
                    {['A', 'B', 'C', 'D'].map((label) => (
                        <button
                            key={label}
                            onClick={() => handleAnswer(label)}
                            disabled={disabled}
                            className={`py-3 px-4 rounded-lg font-bold text-lg transition-all ${
                                selectedAnswer === label
                                    ? 'bg-blue-500 text-white ring-2 ring-blue-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Status indicator */}
                {selectedAnswer && (
                    <div className="status-indicator mt-6 p-4 bg-green-50 border-l-4 border-green-400 rounded">
                        <p className="text-sm text-green-800">
                            ✓ Anda memilih: <strong className="text-lg">{selectedAnswer}</strong>
                        </p>
                    </div>
                )}
            </div>
        );
    };

    /**
     * Render TEXT mode
     * Similar to PT Potensi Kerja
     * Top row: pattern text
     * Bottom: scrambled options
     */
    const renderTextMode = () => {
        const pattern = masterData.pattern || '';
        const pilihan = question.pilihan || [];

        return (
            <div className="spt-potensi-kerja-text">
                {/* Kolom Header */}
                <div className="kolom-header mb-6 text-center">
                    <h2 className="text-3xl font-bold text-gray-800">
                        Kolom {kolomNumber}
                    </h2>
                </div>

                {/* Pattern Display */}
                <div className="pattern-display mb-8 p-6 bg-gray-50 rounded-lg border-2 border-gray-300">
                    <div className="text-center">
                        <div className="text-sm text-gray-600 mb-2">Pola:</div>
                        <div className="pattern-text text-4xl font-mono font-bold text-gray-800 tracking-widest">
                            {pattern.split('').map((char, idx) => (
                                <span key={idx} className="inline-block mx-2 px-3 py-2 bg-white rounded border border-gray-300">
                                    {char}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Instruction */}
                <div className="instruction mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: question.soal }}></p>
                </div>

                {/* Answer Options */}
                <div className="answer-options">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Pilihan Jawaban</h3>

                    <div className="options-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pilihan.map((option, index) => {
                            const optionLabel = String.fromCharCode(65 + index);
                            const isSelected = selectedAnswer === optionLabel;

                            return (
                                <div
                                    key={index}
                                    className={`option-card cursor-pointer p-4 rounded-lg border-2 transition-all ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-400'
                                            : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => handleAnswer(optionLabel)}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`label-badge flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                                            isSelected
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {optionLabel}
                                        </div>
                                        <div className="option-text flex-grow font-mono text-2xl font-bold text-gray-800">
                                            {option}
                                        </div>
                                        {isSelected && (
                                            <div className="checkmark text-3xl text-blue-500">
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Status indicator */}
                {selectedAnswer && (
                    <div className="status-indicator mt-6 p-4 bg-green-50 border-l-4 border-green-400 rounded">
                        <p className="text-sm text-green-800">
                            ✓ Anda memilih: <strong className="text-lg">{selectedAnswer}</strong>
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="question-spt-potensi-kerja">
            {masterType === 'image' ? renderImageMode() : renderTextMode()}
        </div>
    );
};

export default QuestionSPTPotensiKerja;
